import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Spinner } from './spinner'
import { FlowProgress } from './flow-progress'
import { PlatformMark } from './platform-chip'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { PositionView } from '@/lib/api-types'
import { formatAmount, formatRawTokenAmount, formatSol, LAMPORTS_PER_SOL } from '@/lib/format'
import { noteOwnActivity, useInvalidatePortfolio } from '@/lib/portfolio'
// the rail's steps and where a position stands on it live with the server's progress feed, so the chip and this window move alike
import { flowOf, stepOf, stepsOf, type Flow } from '../../../src/api/position-rail'

const TERMINAL = new Set(['open', 'closed', 'failed', 'stalled'])
/** after this long a flow still running says so; it keeps going on its own */
const SLOW_AFTER_MS = 60_000
/** a sale clearing the insider mark first takes a few minutes by design (the wallet's SOL, the pool buy, a short pause) */
const HOUSE_SLOW_AFTER_MS = 4 * 60_000

export type { Flow }

function isDone(flow: Flow, p: PositionView) {
  const status = p.status
  // a sell is done once the proceeds are in: credited at the exchange (that credit is part of the sale, John, Nov 1
  // 2026: "the deposit after the sell to CEX is part of the process") or home; whatever is left over follows on its own.
  // A spot trade in a bank wallet is open the whole time: it is done once the wallet is no longer running it.
  const proceedsHome = status === 'returning' && p.returns.some((hop) => hop.completedAt !== null)
  if (flow === 'buy') return status === 'open' && p.spot === null
  if (flow === 'sell') return (status === 'closed' || (status === 'open' && p.spot === null) || proceedsHome) && !p.proceedsSettling
  // a vault buy is done once the tokens reach the vault; the SOL left over follows on its own
  if (flow === 'vaultBuy') return status === 'closed' || (status === 'returning' && p.custodiedAt !== null)
  return (status === 'closed' || proceedsHome) && !p.proceedsSettling
}
/** a thin muted divider between the parts of a line (John, Nov 1 2026: a muted bar, not bullet points) */
const Sep = () => (
  <span className="mx-1.5 text-muted" aria-hidden="true">
    |
  </span>
)

/**
 * Live progress of a private position: the Relay hop in, the swap, and
 * (for a sell) the hop back out. Polls until the position settles.
 *
 * Without a `positionId` it is the flow being started: the same card, on
 * the rail the flow will run on, saying "Funding…" (or how the start
 * failed) until the record exists and takes over, so the bar never
 * re-mounts or changes shape (John, Nov 1 2026: "one seamless progress UI").
 */
export function PositionProgress({
  positionId,
  mode,
  flow: flowHint,
  exchange = true,
  house,
  starting,
  onSettled,
  onStatus,
}: {
  positionId: string | null
  mode: 'open' | 'sell'
  /** the rail to show before the record is in; by `mode` when unset */
  flow?: Flow
  /** whether that rail ends at an exchange (Depositing) or at home (Returning) */
  exchange?: boolean
  /** a sale that clears the insider mark first: the rail gets its Clearing step; read off the record when unset */
  house?: boolean
  /** how the start is going: a refused start fails the first step, with a way back to the form */
  starting?: { failed: boolean; error: string | null; onBack: () => void }
  onSettled?: () => void
  /** the position's current status, for the caller's own labels */
  onStatus?: (status: PositionView['status']) => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const invalidatePortfolio = useInvalidatePortfolio()
  const query = useQuery({
    ...trpc.position.get.queryOptions({ id: positionId ?? '' }),
    enabled: positionId !== null,
    refetchInterval: (q) => (q.state.data && ((TERMINAL.has(q.state.data.status) && !q.state.data.proceedsSettling && q.state.data.spot === null) || q.state.data.spotFailed) ? false : 1_000),
  })
  const retry = useMutation(
    trpc.position.retry.mutationOptions({
      onSuccess: () => void queryClient.invalidateQueries({ queryKey: trpc.position.get.queryKey({ id: positionId ?? '' }) }),
    }),
  )
  const p = query.data
  const announced = useRef(false)
  // the rail keeps its shape for the whole flow: the caller's word, else the record's the first time it is seen
  const houseRail = useRef<boolean | null>(house ?? null)
  if (houseRail.current === null && p) houseRail.current = p.houseSale
  const houseLabels = houseRail.current ?? false
  const settled = p ? (TERMINAL.has(p.status) && !p.proceedsSettling && p.spot === null) || p.spotFailed || isDone(flowOf(p, mode), p) : false
  // re-render every few seconds so the slow hint appears without new data
  const [, tick] = useState(0)
  useEffect(() => {
    if (settled) return
    const timer = setInterval(() => tick((n) => n + 1), 5_000)
    return () => clearInterval(timer)
  }, [settled])
  useEffect(() => {
    if (settled) onSettled?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled])
  const status = p?.status
  useEffect(() => {
    if (status) onStatus?.(status)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])
  // a buy that was given up: the position returns the SOL and closes with the reason kept
  const abandoned = !!p && mode === 'open' && !!p.lastError && (p.status === 'returning' || p.status === 'closed')
  useEffect(() => {
    if (!p || announced.current || abandoned) return
    const symbol = (p.token.symbol ?? 'token').toUpperCase()
    const flow = flowOf(p, mode)
    if (!isDone(flow, p)) return
    announced.current = true
    // the bar's finished chip says so (John, Nov 1 2026: no separate snackbars)
    noteOwnActivity([symbol, 'SOL', 'USDC', 'USDT'])
    void invalidatePortfolio()
  }, [p, mode, invalidatePortfolio, abandoned])

  if (!p) {
    // the card as it will look, from the moment the flow is started until its first poll: starting is funding
    const flow: Flow = flowHint ?? (mode === 'sell' ? 'vaultSell' : 'vaultBuy')
    const failed = starting?.failed === true
    return (
      <FlowProgress
        labels={stepsOf(flow, exchange, houseLabels)}
        step={0}
        failed={failed}
        status={failed ? (starting?.error ?? 'This did not start.') : 'Funding…'}
        action={
          failed && starting ? (
            <button type="button" className="btn btn-secondary" onClick={starting.onBack}>
              Back
            </button>
          ) : undefined
        }
      />
    )
  }
  const flow = flowOf(p, mode)
  const exchangeSide = p.exchange !== null
  const steps = stepsOf(flow, exchangeSide, houseLabels)
  const sellFlow = flow === 'sell' || flow === 'vaultSell'
  // the proceeds landing at the exchange: the wallet is returning them, or it is already closed or back to open (a bank
  // wallet) while the exchange has yet to credit them (John, Nov 1 2026: the rail fell back to step 1 at that point)
  const depositing = sellFlow && exchangeSide && (p.status === 'returning' || ((p.status === 'closed' || p.status === 'open') && p.proceedsSettling))
  let step = stepOf(flow, p)
  if (depositing) step = steps.length - 2
  // a one-off trade in a bank wallet that stopped: the wallet stays open, so the record says so itself
  const failed = p.status === 'failed' || p.status === 'stalled' || abandoned || p.spotFailed
  const done = !abandoned && !p.spotFailed && isDone(flow, p)
  // a manual trade that stopped: the failure sits on the selling step, the wallet being open again
  if (p.spotFailed) step = Math.max(0, steps.length - 3)
  const startedAt = p.startedAt ? new Date(p.startedAt).getTime() : p.createdAt ? new Date(p.createdAt).getTime() : Date.now()
  const slow = !done && !failed && Date.now() - startedAt > (houseLabels ? HOUSE_SLOW_AFTER_MS : SLOW_AFTER_MS)
  const lastReturn = p.returns[p.returns.length - 1]
  const sellSwap = p.swaps.find((s) => s.side === 'sell' && s.status === 'confirmed' && s.transactionId)
  const tx = (sig: string | null | undefined, label: string) => (sig ? [{ label, href: `https://solscan.io/tx/${sig}` }] : [])
  // done: the buy itself, or the sell; the return of leftover SOL is internal
  const links = done
    ? mode === 'open'
      ? tx(p.swaps.find((s) => s.id === p.buySwapId)?.transactionId, 'View txn')
      : tx(sellSwap?.transactionId ?? lastReturn?.fillTxHash, 'View txn')
    : [...tx(p.fund?.signature, 'Funding tx'), ...tx(sellSwap?.transactionId ?? p.swaps.find((s) => s.id === p.pendingSwapId)?.transactionId, 'Swap tx'), ...tx(lastReturn?.signature, 'Return tx')]
  const symbol = (p.token.symbol ?? '').toUpperCase()
  const sold = p.amountRaw ? formatRawTokenAmount(p.amountRaw, p.token.decimals, symbol) : symbol
  // what reached the exchange: the proceeds paid inside the sell and the leftover that followed
  const proceedsLamports = p.returns.filter((r) => !('currency' in r) || r.currency === null).reduce((sum, r) => sum + Number(r.amountLamports ?? 0), 0)
  const place = p.proceedsExchange ?? p.exchange
  const soldLine = (
    <>
      Sold {sold}
      {proceedsLamports > 0 ? (
        <>
          <Sep />
          {formatSol(proceedsLamports / LAMPORTS_PER_SOL)} SOL{place ? <> at <span className="ml-1 inline-flex"><PlatformMark id={place} size="size-4" /></span></> : null}
        </>
      ) : null}
    </>
  )
  const progress: ReactNode = done
    ? flow === 'vaultBuy'
      ? `Bought ${symbol}`
      : flow === 'buy'
        ? `Bought ${formatAmount(Number(p.tokenBalanceRaw ?? 0) / 10 ** p.token.decimals)} ${symbol}`
        : soldLine
    : abandoned
      ? `${p.lastError} ${p.status === 'closed' ? (p.pool ? 'Your SOL is back in your balance.' : 'Your SOL is back in your wallet.') : 'Your SOL is on its way back.'}`
      : failed
        ? p.spotFailed
          ? `${p.lastError ?? 'This did not finish.'} ${mode === 'sell' ? 'Nothing was sold; try the sale again.' : 'Try again.'}`
          : (p.lastError ?? 'This did not finish.')
        : depositing
          ? (
              <>
                Sold {sold}
                <Sep />
                Depositing to {place ? <span className="mx-1 inline-flex"><PlatformMark id={place} size="size-4" /></span> : 'your exchange'}…
              </>
            )
          : p.status === 'open' && p.spot === 'sell'
            ? (p.houseWait ?? (p.houseStep === 'clearing' ? 'Clearing the insider mark…' : p.houseStep === 'wallet' ? 'Getting the wallet ready…' : `Gathering ${sold}…`))
            : p.status === 'open' && p.spot === 'buy'
              ? 'Funding the buy…'
          : p.status === 'funding'
            ? p.collect
              ? `Gathering from ${p.collect.wallets} wallet${p.collect.wallets === 1 ? '' : 's'}…`
              : p.pool
                ? `Taking ${formatSol(Number(p.fundLamports) / LAMPORTS_PER_SOL)} SOL from your balance…`
                : `Routing ${formatSol(Number(p.fundLamports) / LAMPORTS_PER_SOL)} SOL…`
            : p.status === 'funded' || p.status === 'buying'
              ? 'Buying…'
              : p.status === 'collecting'
                ? 'Collecting your tokens…'
                : p.status === 'custodying'
                  ? 'Depositing…'
                  : p.status === 'selling'
                    ? `Selling ${sold}…`
                    : p.pool
                      ? 'Returning SOL to your balance…'
                      : 'Returning SOL to your wallet…'
  return (
    <FlowProgress
      labels={steps}
      step={step}
      done={done}
      failed={failed}
      status={
        slow ? (
          <span className="flex flex-col items-center gap-1">
            <span className="flex items-center">{progress}</span>
            <span className="text-xs">Taking longer than usual…</span>
          </span>
        ) : (
          <span className="flex items-center justify-center">{progress}</span>
        )
      }
      links={links}
      fees={done ? p.fees : undefined}
      route={done ? (mode === 'open' ? p.swaps.find((s) => s.id === p.buySwapId)?.route : sellSwap?.route) : undefined}
      action={
        failed && !abandoned && !p.spotFailed ? (
          <div className="flex flex-col items-center gap-2">
            <button type="button" className="btn btn-secondary" disabled={retry.isPending} onClick={() => retry.mutate({ id: p.id })}>
              {retry.isPending ? <Spinner /> : 'Retry'}
            </button>
            {retry.isError ? <p className="text-xs text-negative">{errorMessage(retry.error)}</p> : null}
          </div>
        ) : undefined
      }
    />
  )
}
